/**
 * The capture contract, and a placeholder implementation of it.
 *
 * Photo and serial capture belong to `src/capture/`, which a sibling is building. Rather than
 * import a module that may not exist yet — or worse, edit their directory — this file states the
 * props the form renderer needs and provides a minimal implementation that satisfies them.
 *
 * **These two interfaces are the ask.** If `src/capture/` exports components with these props, the
 * only change needed here is swapping the two `use…` defaults for real imports; nothing in
 * `FieldControl` or the schemas changes.
 *
 * The placeholder is deliberately unglamorous — a file input and a text input — because a
 * convincing fake is worse than an obvious one. It is real enough to exercise the engine end to
 * end, and obviously not the shipping component.
 */

import { useCallback, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import type { PhotoRef, SerialValue } from '../types.ts';

/* ------------------------------------------------------------------ the ask ---- */

export interface PhotoCaptureProps {
  /** Field id, so the capture component can namespace anything it persists. */
  readonly fieldId: string;
  /** What to photograph. Shown as the prompt. */
  readonly label: string;
  /** Frames already captured for this field. */
  readonly value: readonly PhotoRef[];
  /** Refuse further capture once this many frames exist. Absent means no cap. */
  readonly max?: number;
  /**
   * Puts raw bytes in the blob store and returns the reference.
   *
   * Supplied by the form layer so a simple capture component does not have to know the blob store
   * exists. A component that already writes through its own sink — as `photoPipeline.ts` does with
   * `BlobSink`, returning a `PhotoAttachment` — can ignore this and call `onCaptured` directly.
   */
  readonly store: (blob: Blob) => Promise<PhotoRef>;
  /**
   * Hands over an already-stored frame. `PhotoAttachment` from `src/capture` is assignable to
   * `PhotoRef`, so no conversion is needed.
   */
  readonly onCaptured: (photo: PhotoRef) => void;
  readonly onRemove: (blobId: string) => void;
  readonly disabled?: boolean;
}

export interface SerialCaptureProps {
  readonly fieldId: string;
  readonly label: string;
  readonly value?: SerialValue | undefined;
  /**
   * The value on the asset record, when there is one, so the technician can compare. Never
   * pre-filled into the input: about 60% of stored serials are migration placeholders, and adopting
   * one as an answer would be recording a reading nobody took.
   */
  readonly reference?: string | undefined;
  /**
   * Called on both scan and manual entry. `method` must reflect what actually happened, and
   * `format` must be null for manual entry — that pair is what makes a disputed serial resolvable
   * later. `SerialCapture` from `src/capture` is assignable to `SerialValue`.
   */
  readonly onCaptured: (value: SerialValue) => void;
  readonly minLength?: number;
  readonly disabled?: boolean;
}

/* -------------------------------------------------------------- placeholder ---- */

/** Placeholder photo capture: a file input. Obviously not the shipping component. */
export function PlaceholderPhotoCapture(props: PhotoCaptureProps): JSX.Element {
  const { fieldId, label, value, max, store, onCaptured, onRemove, disabled } = props;
  const input = useRef<HTMLInputElement>(null);
  const atCap = max !== undefined && value.length >= max;

  const pick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;
      const picked = Array.from(files);
      event.target.value = '';
      void (async () => {
        for (const file of picked) onCaptured(await store(file));
      })();
    },
    [onCaptured, store],
  );

  return (
    <div className="fx-photo" data-field={fieldId}>
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        multiple={max === undefined || max > 1}
        onChange={pick}
        disabled={disabled === true || atCap}
        aria-label={label}
      />
      {value.length > 0 && (
        <ul className="fx-photo-list">
          {value.map((photo) => (
            <li key={photo.blobId}>
              <span className="fx-photo-id">{photo.label ?? photo.blobId}</span>
              <button type="button" onClick={() => onRemove(photo.blobId)} disabled={disabled === true}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {atCap && <p className="fx-hint">Maximum of {max} reached.</p>}
      <p className="fx-hint fx-placeholder-note">Placeholder capture — the real component comes from src/capture.</p>
    </div>
  );
}

/** Placeholder serial capture: a text input that records `method: 'manual'` honestly. */
export function PlaceholderSerialCapture(props: SerialCaptureProps): JSX.Element {
  const { fieldId, label, value, reference, onCaptured, minLength, disabled } = props;
  const [draft, setDraft] = useState(value?.serial ?? '');

  return (
    <div className="fx-serial" data-field={fieldId}>
      {reference !== undefined && (
        <p className="fx-reference">
          On the asset record: <code>{reference}</code>. Roughly 60% of stored serials are migration
          placeholders, so treat this as something to check, not as the answer.
        </p>
      )}
      <input
        type="text"
        inputMode="text"
        autoCapitalize="characters"
        spellCheck={false}
        value={draft}
        aria-label={label}
        disabled={disabled === true}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const serial = draft.trim();
          if (serial === '') return;
          if (minLength !== undefined && serial.length < minLength) return;
          // `manual` with a null format, never `scan`. The placeholder cannot scan, and claiming a
          // symbology it never read would put false evidence on the record.
          onCaptured({ serial, raw: draft, method: 'manual', format: null, at: Date.now() });
        }}
      />
      {value?.method === 'scan' && (
        <p className="fx-hint">Scanned{value.format ? ` (${value.format})` : ''}.</p>
      )}
      <p className="fx-hint fx-placeholder-note">Placeholder capture — no barcode scan. src/capture supplies that.</p>
    </div>
  );
}
